// App-wide UI scaling for smaller desktop / laptop screens.
//
// Every page is built in fixed px against a 1920px-wide Figma frame. Below
// that width the whole UI is shrunk proportionally via `body { zoom }`, so a
// MacBook (~1440px) shows the same layout as a 1920px monitor, just smaller.
//
// Consequences every component must respect (Chrome/Edge/Firefox "standard"
// CSS zoom):
//   - `vh` units inside <body> are scaled by the zoom too, so `100vh` would
//     stop short of the window bottom. Use `calc(var(--vh, 1vh) * N)` instead;
//     --vh is kept at (window height / zoom / 100) px.
//   - getBoundingClientRect() and mouse event coordinates are in screen px,
//     while CSS lengths you set are multiplied by the zoom. Divide measured
//     values by getAppZoom() before writing them back as CSS px.

const DESIGN_WIDTH = 1920;
/** The long-standing zoom the app has used at full design width. */
const BASE_ZOOM = 0.98;
/** Floor so body text (13px) never renders below ~10px. */
const MIN_ZOOM = 0.75;

let currentZoom = BASE_ZOOM;

export function getAppZoom(): number {
  return currentZoom;
}

/**
 * Width the scale is based on. Uses the screen / window size rather than
 * `innerWidth`: in Chrome and Edge neither changes with browser zoom (Ctrl +/-),
 * so a user enlarging the page isn't immediately shrunk back by this scaling.
 */
function basisWidth(): number {
  const candidates = [window.screen?.width, window.outerWidth].filter(
    (w): w is number => typeof w === "number" && w > 0
  );
  return candidates.length ? Math.min(...candidates) : window.innerWidth;
}

export function computeAppZoom(width: number): number {
  const scaled = BASE_ZOOM * Math.min(1, width / DESIGN_WIDTH);
  return Math.round(Math.max(MIN_ZOOM, scaled) * 1000) / 1000;
}

export function applyAppZoom(zoom: number = computeAppZoom(basisWidth())): void {
  currentZoom = zoom;
  const root = document.documentElement.style;
  root.setProperty("--app-zoom", String(zoom));
  root.setProperty("--vh", `${window.innerHeight / zoom / 100}px`);
}

/**
 * Apply now and keep in sync with window resizes. Call once, before render:
 * registering first means components' own resize handlers (which may call
 * getAppZoom()) always see the updated value. Applying is two custom-property
 * writes, cheap enough to run synchronously on every resize event.
 */
export function initAppZoom(): void {
  applyAppZoom();
  window.addEventListener("resize", () => applyAppZoom());
}
